import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { PlusFill } from '@lyearn/icons';
import {
  AutocompleteRenderOptionState,
  Divider,
  Select,
  Stack,
  Typography,
  TypographyColors,
} from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';

import AgendaTemplate from '../Template';
import useAgendaTemplates from '../Template/hooks/useAgendaTemplates';
import { getTemplate } from '../Template/utils';

type OptionType = {
  label: string;
  value: string;
};

export const EMPTY_TEMPLATE_ID = 'default';
const NEW_ID = 'new';

function SelectOptionItem({
  labelColor,
  value,
}: {
  labelColor: TypographyColors;
  value: OptionType;
}) {
  const { label } = value;

  if (value.value === NEW_ID) {
    return (
      <Stack className="w-full hover:bg-bg-tertiary" direction="col">
        <Stack alignItems="center" className="w-full truncate" gapX={6}>
          <PlusFill fillColorToken="link-internal" height={22} width={22} />
          <Typography className="truncate" color={labelColor} variant="body-short-01">
            {label}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" className="truncate hover:bg-bg-tertiary" gapX={12}>
      <Typography className="truncate" color={labelColor} variant="body-short-01">
        {label}
      </Typography>
    </Stack>
  );
}

const renderValue = (option: OptionType) => (
  <SelectOptionItem labelColor="text-text-primary" value={option} />
);

const optionLabelRenderer = (option: OptionType, state: AutocompleteRenderOptionState) => (
  <SelectOptionItem
    labelColor={state.selected ? 'text-text-primary' : 'text-text-secondary'}
    value={option}
  />
);

const SelectTemplateMenu = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string | null) => void;
}) => {
  const { t } = useTranslation(['1on1s', 'common']);
  const { fetching, templates } = useAgendaTemplates();
  const [isTemplatesModalOpen, { toggle: toggleIsTemplatesModalOpen }] = useToggle(false);

  const miniTemplates =
    templates?.map((template) => ({
      label: getTemplate(t, template),
      value: template.id,
    })) ?? [];

  const allValues: {
    label: string;
    value: string;
  }[] = [
    {
      label: t('1on1s:Modal.NoTemplate'),
      value: EMPTY_TEMPLATE_ID,
    },
    ...miniTemplates,
    {
      label: t('1on1s:Templates.CreateNew'),
      value: NEW_ID,
    },
  ];

  return (
    <Stack className="h-full w-full" direction="col">
      <Divider className="mt-8 mb-32" />
      <Stack direction="col">
        <Typography className="mb-8 ms-8" variant="body-short-02">
          {t('1on1s:Modal.DefaultTemplate')}
        </Typography>
        <Select
          disableClearable
          getOptionLabel={getSelectOptionLabel}
          loading={fetching}
          optionLabelRenderer={optionLabelRenderer}
          options={allValues}
          placeholder={t('Common:Actions.Select')}
          renderValue={renderValue}
          size="large"
          value={value}
          onChange={async (value) => {
            if (value === 'new') {
              return toggleIsTemplatesModalOpen();
            }
            onChange(value ?? null);
          }}
        />
      </Stack>
      {isTemplatesModalOpen ? (
        <AgendaTemplate
          isCreatingANewTemplate
          open={isTemplatesModalOpen}
          onSelectTemplate={(template) => {
            onChange(template.id);
            toggleIsTemplatesModalOpen();
          }}
          onToggle={toggleIsTemplatesModalOpen}
        />
      ) : null}
    </Stack>
  );
};

export default SelectTemplateMenu;
